import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { ReactComponent as TwitterIcon } from '../../images/icon/social-twitter-x-system.svg';
import { Icon } from '@legacy-vfuk/core-icon';
import { Paragraph } from '@legacy-vfuk/core-paragraph';
import { link } from 'fs';
import fireButtonAndLinkClicksEvent from '../../helpers/fireButtonAndLinkClicksEvent';

const container = css`
  display: flex;
  gap: 32px;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding-top: 10px;
  }
`;

const iconContainer = css`
  display: flex;
  gap: 24px;
  align-items: center;

  @media (max-width: 400px) {
    gap: 12px;
  }
`;

const iconSpacer = css`
  display: flex;
  cursor: default;
`;

const SocialButtonsLabelWrapper = styled.div`
  [class*='Paragraphstyle__Paragraph-sc'] {
    white-space: nowrap;
  }
`;

const IconWrapper = styled.div`
  cursor: pointer;
  display: flex;
`;

const XIconFillOverride = css`
  fill: white;
`;

// The Social media labels coming in from contentful. If 'all' exists, then show all social media buttons,
// otherwise show the ones that appear from 'all', 'mail', 'Facebook',Google+', 'LinkedIn', 'Twitter'.
enum SocialName {
  all = 'all',
  mail = 'mail',
  facebook = 'Facebook',
  linkedIn = 'LinkedIn',
  twitter = 'Twitter',
}
// The flag property names that should be true/false based on the received Social media labels.
// These are the flags that actually drive the view.
enum SocialFlag {
  hasMail = 'hasMail',
  hasFacebook = 'hasFacebook',
  hasLinkedIn = 'hasLinkedIn',
  hasTwitter = 'hasTwitter',
  hasAnySocial = 'hasAnySocial',
}

enum SocialUrl {
  facebook = 'https://www.facebook.com/sharer/sharer.php?u=[pageUrl]',
  linkedIn = 'https://www.linkedin.com/cws/share?url=[pageUrl]',
  twitter = 'https://twitter.com/intent/tweet?url=[pageUrl]',
}

type SocialMediaFlags = {
  hasMail: boolean;
  hasFacebook: boolean;
  hasLinkedIn: boolean;
  hasTwitter: boolean;
  hasAnySocial: boolean;
};

type SectionElementSocialMediaProps = {
  socialMediaButtons: Array<SocialName>;
  socialMediaButtonsLabel: string;
  seoTitle: string;
};

const SectionElementSocialMedia = ({
  socialMediaButtons,
  socialMediaButtonsLabel,
  seoTitle,
}: SectionElementSocialMediaProps) => {
  const { hasMail, hasFacebook, hasLinkedIn, hasTwitter, hasAnySocial } =
    getSocialMediaFlags(socialMediaButtons);
  const mailUrl = `mailto:?subject=${seoTitle} | Vodafone&body=[pageUrl]`;
  const facebookUrl = SocialUrl.facebook;
  const linkedInUrl = SocialUrl.linkedIn;
  const twitterUrl = SocialUrl.twitter;

  if (!hasAnySocial) {
    return null;
  }

  function checkWindowSize() {
    const [isMobile, setIsMobile] = useState(false);
    if (typeof window !== 'undefined') {
      useEffect(() => {
        function handleResize() {
          if (window.innerWidth <= 768) {
            setIsMobile(true);
          } else {
            setIsMobile(false);
          }
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
      }, []);
    }
    return isMobile;
  }

  const isMobile = checkWindowSize();

  return (
    <div css={container}>
      <SocialButtonsLabelWrapper>
        <Paragraph size={2} weight={2} inverse noMargin>
          {socialMediaButtonsLabel}
        </Paragraph>
      </SocialButtonsLabelWrapper>

      <div css={iconContainer}>
        {hasFacebook && (
          <a onClick={() => openPopup(facebookUrl)} css={iconSpacer}>
            <IconWrapper>
              <Icon
                name="facebook"
                group="brandLofi"
                inverse
                size={isMobile ? 3 : 2}
              />
            </IconWrapper>
          </a>
        )}
        {hasLinkedIn && (
          <a onClick={() => openPopup(linkedInUrl)} css={iconSpacer}>
            <IconWrapper>
              <Icon
                name="linkedin"
                group="brandLofi"
                inverse
                size={isMobile ? 3 : 2}
              />
            </IconWrapper>
          </a>
        )}
        {hasTwitter && (
          <a onClick={() => openPopup(twitterUrl)} css={iconSpacer}>
            <IconWrapper>
              <TwitterIcon css={XIconFillOverride} />
            </IconWrapper>
          </a>
        )}
        {hasMail && (
          <a onClick={() => openLink(mailUrl)} css={iconSpacer}>
            <IconWrapper>
              <Icon name="mail" inverse size={3} isResponsive={false} />
            </IconWrapper>
          </a>
        )}
      </div>
    </div>
  );
};

// The lookup data item that converts existence of a Social media labels to flags
// (if the label exists, all flags should be set true)
type LookupItem = {
  name: SocialName;
  flags: Array<SocialFlag>;
};

const getSocialMediaFlags = (
  socialMediaButtons: Array<string>
): SocialMediaFlags => {
  const lookup: Array<LookupItem> = [
    {
      name: SocialName.all,
      flags: [
        SocialFlag.hasMail,
        SocialFlag.hasFacebook,
        SocialFlag.hasLinkedIn,
        SocialFlag.hasTwitter,
        SocialFlag.hasAnySocial,
      ],
    },
    {
      name: SocialName.mail,
      flags: [SocialFlag.hasMail, SocialFlag.hasAnySocial],
    },
    {
      name: SocialName.facebook,
      flags: [SocialFlag.hasFacebook, SocialFlag.hasAnySocial],
    },
    {
      name: SocialName.linkedIn,
      flags: [SocialFlag.hasLinkedIn, SocialFlag.hasAnySocial],
    },
    {
      name: SocialName.twitter,
      flags: [SocialFlag.hasTwitter, SocialFlag.hasAnySocial],
    },
  ];
  const flags: SocialMediaFlags = {
    hasMail: false,
    hasFacebook: false,
    hasLinkedIn: false,
    hasTwitter: false,
    hasAnySocial: false,
  };

  lookup.forEach((item: LookupItem) => {
    if (socialMediaButtons.includes(item.name)) {
      item.flags.forEach(prop => (flags[prop] = true));
    }
  });
  return flags;
};

async function handleLinkClick(url: string) {
  switch (url) {
    case SocialUrl.facebook:
      return fireButtonAndLinkClicksEvent({
        event_label: 'Facebook',
        event_action: 'Social Share Click',
      });
    case SocialUrl.linkedIn:
      return fireButtonAndLinkClicksEvent({
        event_label: 'LinkedIn',
        event_action: 'Social Share Click',
      });
    case SocialUrl.twitter:
      return fireButtonAndLinkClicksEvent({
        event_label: 'Twitter/X',
        event_action: 'Social Share Click',
      });
    default:
      return fireButtonAndLinkClicksEvent({
        event_label: 'Mail',
        event_action: 'Social Share Click',
      });
  }
}

async function openPopup(url: string) {
  const left = window.screenLeft + 10;
  const top = window.screenTop + 50;
  const liveUrl = url.replace('[pageUrl]', window.location.href);
  // NB - we can't use window.location.href until after initial render, which is why
  // we replace it onClick; saves having to use a dirty useEffect!
  await handleLinkClick(url);
  window.open(
    liveUrl,
    '_blank',
    `toolbar=0,location=0,menubar=0,height=500,width=700,left=${left},top=${top}`
  );
}

async function openLink(url: string) {
  const liveUrl = url.replace('[pageUrl]', window.location.href);
  await handleLinkClick(url);
  window.location.href = liveUrl;
}

export default SectionElementSocialMedia;
